import { useCallback, useEffect, useRef, useState } from 'react';
import { errorMessage, newIdemKey } from './api';

/**
 * Load data with explicit loading / error / empty states.
 * `fn` returns a promise; it re-runs when `deps` change or on `reload()`.
 * A failed load keeps the last good data and exposes `error` so screens can
 * show "couldn't load" separately from "nothing here".
 */
export function useFetch(fn, deps = []) {
  const [state, setState] = useState({ data: null, error: null, loading: true });
  const seq = useRef(0);
  const run = useCallback(async () => {
    const my = ++seq.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await fn();
      if (my === seq.current) setState({ data, error: null, loading: false });
    } catch (err) {
      if (my === seq.current) setState((s) => ({ ...s, error: err, loading: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => { run(); }, [run]);
  return { ...state, reload: run, errorText: state.error ? errorMessage(state.error) : '' };
}

/**
 * Run a mutating action with busy + inline error state. The idempotency key
 * stays the same across retries of one attempt and is renewed after success,
 * so a retried network failure is never applied twice by the server.
 */
export function useAction(fn, { onSuccess } = {}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const keyRef = useRef(newIdemKey());
  const run = useCallback(async (...args) => {
    setBusy(true);
    setError(null);
    try {
      const out = await fn(...args, keyRef.current);
      keyRef.current = newIdemKey();
      if (onSuccess) await onSuccess(out);
      return out;
    } catch (err) {
      setError(err);
      return undefined;
    } finally {
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fn, onSuccess]);
  const reset = useCallback(() => { setError(null); keyRef.current = newIdemKey(); }, []);
  return { run, busy, error, errorText: error ? errorMessage(error) : '', errorCode: error?.flCode, reset };
}

/** Keep a value in sessionStorage so a refresh doesn't lose a draft form. */
export function useSessionState(key, initial) {
  const [v, setV] = useState(() => {
    try {
      const raw = sessionStorage.getItem(key);
      return raw ? JSON.parse(raw) : initial;
    } catch { return initial; }
  });
  useEffect(() => {
    try { sessionStorage.setItem(key, JSON.stringify(v)); } catch { /* storage unavailable */ }
  }, [key, v]);
  const clear = useCallback(() => {
    try { sessionStorage.removeItem(key); } catch { /* ignore */ }
  }, [key]);
  return [v, setV, clear];
}

/** Text download for CSV exports and templates. */
export function downloadText(text, filename, type = 'text/csv') {
  const blob = new Blob([text], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
